import { IPayHistory, IPayHistoryChangeEntry } from 'typings/payHistory';
import { PayHistoryChangeEntry } from './payHistoryChangeEntry';
import { TeamPageViews } from 'nomst/Stores/Configuration/teamPageViews';
import { EmployeeClass } from 'typings/enums';
import { compareItem } from 'common/compareItem';
import { defaultStandardWorkHours, defaultWeeksPerYear } from 'common/constants';
import AmountConvert from 'common/amountConvert';
import * as dateFns from 'date-fns';

export class PayHistory implements IPayHistory {
    private _payHistoryId: number;
    private _employeeSourceId: string;
    private _hireDate: Nullable<string>;
    private _countryCode: Nullable<string>;
    private _isoCurrencyCode: Nullable<string>;
    private _weeklyStandardWorkHours: Nullable<number>;
    private _employeeClass: EmployeeClass;
    private _startingBaseSalary: Nullable<number>;
    private _startingHourlyRate: Nullable<number>;
    private _title: Nullable<string>;
    private _payHistoryChangeEntries: PayHistoryChangeEntry[];

    constructor(payHistoryDto: IPayHistory, teamPageViews: TeamPageViews) {
        this._payHistoryId = payHistoryDto.payHistoryId;
        this._employeeSourceId = payHistoryDto.employeeSourceId;
        this._hireDate = payHistoryDto.hireDate;
        this._countryCode = payHistoryDto.countryCode;
        this._isoCurrencyCode = payHistoryDto.isoCurrencyCode;
        this._weeklyStandardWorkHours = payHistoryDto.weeklyStandardWorkHours;
        this._employeeClass = payHistoryDto.employeeClass;
        this._startingBaseSalary = payHistoryDto.startingBaseSalary;
        this._startingHourlyRate = payHistoryDto.startingHourlyRate;
        this._payHistoryChangeEntries = payHistoryDto.payHistoryChangeEntries
            ? payHistoryDto.payHistoryChangeEntries.map(entry =>
                new PayHistoryChangeEntry(entry, this._employeeSourceId, teamPageViews))
            : [];
    }

    get payHistoryId(): number {
        return this._payHistoryId;
    }

    get employeeSourceId(): string {
        return this._employeeSourceId;
    }

    get countryCode(): Nullable<string> {
        return this._countryCode;
    }

    get isoCurrencyCode(): Nullable<string> {
        return this._isoCurrencyCode;
    }

    get weeklyStandardWorkHours(): number {
        return this._weeklyStandardWorkHours || defaultStandardWorkHours;
    }

    get employeeClass(): EmployeeClass {
        return this._employeeClass;
    }

    get startingBaseSalary(): Nullable<number> {
        return this._startingBaseSalary;
    }

    get startingHourlyRate(): Nullable<number> {
        return this._startingHourlyRate;
    }

    get title(): Nullable<string> {
        return this._title;
    }

    get isHourly(): boolean {
        return this._employeeClass === EmployeeClass.Hourly;
    }

    get hireDate(): Nullable<string> {
        return this._hireDate;
    }

    get hireDateType(): Nullable<Date> {
        return this._hireDate ? new Date(this._hireDate) : null;
    }

    get payHistoryChangeEntries(): PayHistoryChangeEntry[] {
        return this._payHistoryChangeEntries;
    }

    get payHistoryChangeEntryYears(): number[] {
        if (!this._payHistoryChangeEntries.length) {
            return [];
        }

        const yearsEnd = this.payChangeYearsEnd();
        const startingYear = this.startingPayChangeYear() || 0;
        return Array.from(
            { length: (yearsEnd - startingYear) + 1 },
            (value, index) => yearsEnd - index);
    }

    get annualStandardWorkHoursOnHiredYear(): number {
        return (this.weeklyStandardWorkHours || defaultStandardWorkHours) * defaultWeeksPerYear;
    }

    annualBasePay(): number {
        return AmountConvert
            .convertToAnnual(this.startingBasePay(), this.annualStandardWorkHoursOnHiredYear, this.isHourly);
    }

    payEffectiveDateYears(): Date[] {
        if (!this._payHistoryChangeEntries) {
            return [];
        }

        return this._payHistoryChangeEntries
            .map(payHistoryChangeEntry => payHistoryChangeEntry.payEffectiveDateType);
    }

    payChangeYearsEnd(): number {
        return dateFns.max(this.payEffectiveDateYears()).getFullYear();
    }

    startingPayChangeYear(): Nullable<number> {
        if (this.isHiredOnEntryValid()) {
            return this.hiredOnPayHistoryYear();
        }

        return dateFns.min(this.payEffectiveDateYears()).getFullYear();
    }

    startingBasePay(): number {
        return (this.isHourly ? this._startingHourlyRate : this._startingBaseSalary) || 0;
    }

    isHiredOnEntryValid(): boolean {
        return (this._hireDate !== null && this.startingBasePay() !== 0);
    }

    hiredOnPayHistoryYear(): Nullable<number> {
        return (this._hireDate && this.hireDateType) ? this.hireDateType.getFullYear() : null;
    }

    sortedPayHistoryEntriesOfTheYear(year: number, payHistoryFilter: string): Nullable<PayHistoryChangeEntry[]> {
        // There's actually a subtle bug that this fixes that only pops up at the end of the year.
        // Because it's a UTC date but date-fns only works in local time zone there's a time difference
        // as far as the problem. Easy solution to this is just get a new UTC date, but in the month of
        // Feb so that there's no wiggle room of rolling over the calendar year.
        const yearDate = Date.UTC(year, 2);
        let payHistoryChangeEntries = this._payHistoryChangeEntries.filter(
            payHistoryChangeEntry => dateFns.isSameYear(payHistoryChangeEntry.payEffectiveDateType, yearDate));
        if (payHistoryChangeEntries.length === 0) {
            return (year === this.hiredOnPayHistoryYear()) ? null : [];
        }

        if (payHistoryFilter.length > 0) {
            payHistoryChangeEntries = payHistoryChangeEntries.filter(payHistory =>
                payHistory.amounts?.some(amount => amount.amountCode === payHistoryFilter));
        }

        return payHistoryChangeEntries.sort((item1: IPayHistoryChangeEntry, item2: IPayHistoryChangeEntry) =>
            compareItem(item1.payEffectiveDateType, item2.payEffectiveDateType, false));
    }

    filteredSortedPayHistoryEntries(payHistoryFilter: string): PayHistoryChangeEntry[] {
        const payHistoryChangeEntries = this._payHistoryChangeEntries;
        // filter pay history amounts to only show when their amountCode matches the current filter
        return payHistoryChangeEntries.filter(payHistory =>
            payHistory.amounts?.some(amount => amount.amountCode === payHistoryFilter)
        ).sort((item1: IPayHistoryChangeEntry, item2: IPayHistoryChangeEntry) =>
            compareItem(item1.payEffectiveDateType, item2.payEffectiveDateType, false)
        );
    }
}